import { Theme } from '@mui/material/styles';
import { PricingEventRuleConditionType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isEqual, snakeCase } from 'lodash-es';

import {
  AddOnSnapshot,
  PricingEvent,
  PricingEventAction,
  PricingEventChange,
  PricingEventChangeValue,
  PricingEventRuleCondition,
} from '~hooks/usePricing/models';

// The billing parties (each rate has one). These get a colored dot in the timeline.
export type PricingParty = 'customer' | 'vendor' | 'driver';

// Every change belongs to a group, identified by its snake_case dim prefix ("customer_rate_value"
// -> customer, "material_rate_buy_price" -> material, "tax_rate_percentage" -> tax). Groups beyond
// the billing parties (material, tax) read as sections alongside them. Anything else is null.
export type PricingGroup = PricingParty | 'material' | 'tax';

const PARTY_ORDER: PricingParty[] = ['customer', 'vendor', 'driver'];
const GROUP_ORDER: PricingGroup[] = [...PARTY_ORDER, 'material', 'tax'];

const isParty = (group: PricingGroup): group is PricingParty =>
  (PARTY_ORDER as string[]).includes(group);

// Group dot colors. The billing parties get distinct hues (palette has no purple, so driver uses
// green); material and tax are categories, not parties, so they take a neutral gray dot.
export const getGroupDotColor = (theme: Theme): Record<PricingGroup, string> => ({
  customer: theme.brandV2.colors.treadBlue,
  vendor: theme.brandV2.colors.treadOrange,
  driver: theme.brandV2.colors.treadGreen,
  material: theme.brandV2.colors.treadGray4,
  tax: theme.brandV2.colors.treadGray4,
});

export const groupLabel = (group: PricingGroup) => {
  const labelByGroup: Record<PricingGroup, string> = {
    customer: t('loads.pricing.customer'),
    vendor: t('loads.pricing.vendor'),
    driver: t('loads.pricing.driver'),
    material: t('loads.pricing.material'),
    tax: t('loads.pricing.tax'),
  };

  return labelByGroup[group];
};

const groupOfDim = (dim: string): PricingGroup | null =>
  GROUP_ORDER.find((group) => dim.startsWith(`${group}_`)) ?? null;

// Internal / low-signal dims that carry no real meaning to a human reading the history: raw ids
// (`*_id` — e.g. site_rate_schedule_id, material_id — are UUIDs) and the `*_resolution` flags
// (matched/inherited/frozen).
//
// Kept separate from `isHiddenDim` on purpose. The lock-event and rate-kind checks below classify by
// what is INTERNAL, not by what is currently hidden — TRE-16129 adds `*_locked` to the hidden list,
// and both checks need those markers to stay visible to them.
const isInternalDim = (dim: string): boolean =>
  dim.endsWith('_id') || dim.endsWith('_resolution');

// A `*_locked` marker: bookkeeping recording that a dimension was frozen by invoice approval
// (TRE-15925). Meaningless as a before/after row, but it is what identifies a lock event and names
// the party the lock applied to.
const isLockMarkerDim = (dim: string): boolean => dim.endsWith('_locked');

// Dims hidden from the timeline's change rows: the internal ones above, plus the `*_locked` booleans
// an approval flips (TRE-16129 — "Rate locked: — -> false" is machine talk, and a seed event carries
// seven of them).
//
// Hidden from the CHANGE ROWS only — the dims stay on the model, because TRE-16131 derives the
// locked party from them for the headline. A lock event's diff is made up ENTIRELY of these markers,
// so hiding them without the milestone guard in `eventHasVisibleChanges` would drop the event itself
// and erase "rates locked by approval" from the history.
const isHiddenDim = (dim: string): boolean => isInternalDim(dim) || isLockMarkerDim(dim);

// Which of an action's records names the merged entry. One atomic change writes a record per grain,
// so without a deliberate rule the entry's summary, timestamp and actor all come from whichever row
// the API happened to return first (TRE-16130).
//
// Broadest wins: an action that wrote at BOTH order and job grain STARTED at the order — the job
// records are its consequences, so naming it "Job" hides the cause. This is safe by construction on
// the backend, not just by convention: `PricingService::ManualOverride` rejects any subject that is
// not a Load / JobAssignment / Job, so a single-job edit cannot emit an Order record and can never
// be relabelled as an order event. Job and JobAssignment are ONE grain — a JA is how a job is
// dispatched, and a reader does not distinguish them.
const GRAIN_RANK: Record<string, number> = {
  Order: 0,
  Job: 1,
  JobAssignment: 1,
  Load: 2,
};

// An unrecognized subject_type sorts last instead of throwing, so a grain added by the backend
// cannot take over an entry's identity before this file knows how to describe it.
const UNRANKED_GRAIN_RANK = 3;

const grainRank = (event: PricingEvent): number =>
  GRAIN_RANK[event.subjectType] ?? UNRANKED_GRAIN_RANK;

/**
 * One collapsed timeline entry: the event that names it, plus how far its action reached. The reach
 * cannot be read off the entry itself — it is a property of the records that were merged away — so
 * it travels alongside rather than being faked onto the model.
 */
export interface MergedPricingEntry {
  event: PricingEvent;
  /**
   * Distinct jobs the action touched, in the records' sorted order. Empty for an order-only action.
   * Holds the refs rather than a count so the single-job case can name its job even when an
   * Order-grain record won the entry and carries no `subjectJobRef` itself.
   */
  jobRefs: string[];
}

// Total order over one action's records, so nothing about the merged entry depends on API row
// order. Ties at the same grain are the norm rather than the exception: an order-wide cascade writes
// one record per job, and an audience-split action writes two records at the SAME grain (TRE-15433).
// Oldest comes next because the first record written sits closest to the action's origin, and `id`
// is the deterministic backstop for records sharing a timestamp.
const compareForBase = (first: PricingEvent, second: PricingEvent): number =>
  grainRank(first) - grainRank(second) ||
  first.occurredAt.valueOf() - second.occurredAt.valueOf() ||
  first.id.localeCompare(second.id);

// One atomic pricing change is written as several event records sharing an action_id, in two
// shapes: the same diff repeated at each grain (Order / JobAssignment / Load), and — on orders
// with a connected counterparty (TRE-15401) — the diff SPLIT by audience, customer-side dims on a
// company_scoped record and vendor-side dims on a global one. Collapse each action to one entry
// carrying the union of its records' changes: a change whose (dim, from, to) already appears is a
// grain repeat and is skipped; anything else is a distinct half of a split and must be kept
// (TRE-15433 — the old keep-first collapse silently dropped the vendor half). Deep equality
// because *_add_ons values are arrays of objects. Merged entries are fresh copies (asMergedEntry) —
// the inputs are React Query cache objects shared with other consumers.
//
// Each action's records are sorted by `compareForBase` first, so every part of the resulting entry
// is decided by the data rather than by row order: the grain that names it, the order its change
// rows appear in, and which record supplies the actor. `occurredAt` is the EARLIEST across the
// records (when the action started, not when the winning grain's row was written), and `actor` and
// the rule are taken from the first record in sorted order that has one — a system record carries
// no actor, and only rule-driven sites stamp a rule, so taking the winner's null would drop a value
// a sibling record still has.
//
// `jobRefs` collects the DISTINCT jobs the action touched, which only the pre-merge records know:
// an Order-subject record contributes none (`subject_job_id` is nil for an Order subject by
// design), and each Job/JobAssignment/Load record contributes its own job.
export const mergeEventsByActionId = (events: PricingEvent[]): MergedPricingEntry[] => {
  const recordsByActionId = new Map<string, PricingEvent[]>();
  const recordGroups: PricingEvent[][] = [];

  events.forEach((event) => {
    const existing = event.actionId ? recordsByActionId.get(event.actionId) : undefined;

    if (existing) {
      existing.push(event);

      return;
    }

    const group = [event];
    if (event.actionId) {
      recordsByActionId.set(event.actionId, group);
    }
    recordGroups.push(group);
  });

  return recordGroups.map((group) => {
    const records = [...group].sort(compareForBase);
    const [base] = records;

    let changes: PricingEventChange[] | null = null;
    records.forEach((record) => {
      if (!record.changes?.length) {
        return;
      }
      const collected = changes ?? [];
      record.changes.forEach((change) => {
        const isGrainRepeat = collected.some(
          (alreadyCollected) =>
            alreadyCollected.dim === change.dim &&
            isEqual(alreadyCollected.from, change.from) &&
            isEqual(alreadyCollected.to, change.to),
        );
        if (!isGrainRepeat) {
          collected.push(change);
        }
      });
      changes = collected;
    });

    const earliestOccurredAt = records.reduce(
      (earliest, record) =>
        record.occurredAt.isBefore(earliest) ? record.occurredAt : earliest,
      base.occurredAt,
    );

    // Collected from the records rather than read off the winner: an Order-grain winner has no
    // `subjectJobRef` of its own, so a single-job order-level cascade would otherwise lose the one
    // job it touched and render with no suffix at all.
    const jobRefsTouched = new Set(
      records
        .map((record) => record.subjectJobRef)
        .filter((jobRef): jobRef is string => jobRef !== null),
    );

    // Name and conditions describe ONE card, so both come off the same record — resolving them
    // separately could pair one card's name with another's conditions.
    const ruleRecord = records.find((record) => record.ruleName);

    return {
      event: base.asMergedEntry({
        actor: records.find((record) => record.actor)?.actor ?? null,
        changes,
        occurredAt: earliestOccurredAt,
        ruleConditions: ruleRecord?.ruleConditions ?? [],
        ruleName: ruleRecord?.ruleName ?? null,
      }),
      jobRefs: [...jobRefsTouched],
    };
  });
};

// Each party's rate type in effect at one event: `from` before the change, `to` after. For a
// value-only event both sides hold the carried-forward type; absent when the type is unknown.
type EffectiveRateTypes = Partial<Record<PricingParty, { from?: string; to?: string }>>;

// The pricing_events payload only carries a party's `*_rate_type` when it actually changed — a
// value-only edit omits it — but the type is always emitted alongside the value whenever it does
// change. Walk the events oldest-first, carrying each party's last-known rate type forward, so a
// value-only change can still show the type in effect ("$5.50 Per Ton -> $6.00 Per Ton"). Returns
// the resolved before/after type per party, keyed by event id.
export const resolveEffectiveRateTypes = (
  events: PricingEvent[],
): Map<string, EffectiveRateTypes> => {
  const resolvedByEvent = new Map<string, EffectiveRateTypes>();
  const currentTypeByParty: Partial<Record<PricingParty, string>> = {};

  // Events arrive newest-first; process chronologically so "current" reflects prior history.
  [...events].reverse().forEach((event) => {
    const changes = event.changes ?? [];
    const resolved: EffectiveRateTypes = {};

    PARTY_ORDER.forEach((party) => {
      const typeChange = changes.find((change) => change.dim === `${party}_rate_type`);
      const changedValue = changes.some((change) => change.dim === `${party}_rate_value`);

      if (typeChange) {
        const from =
          typeof typeChange.from === 'string'
            ? typeChange.from
            : currentTypeByParty[party];
        const to = typeof typeChange.to === 'string' ? typeChange.to : undefined;
        resolved[party] = { from, to };
        currentTypeByParty[party] = to ?? currentTypeByParty[party];
      } else if (changedValue) {
        // Value-only change: the type held steady, so both sides use the carried-forward type.
        resolved[party] = {
          from: currentTypeByParty[party],
          to: currentTypeByParty[party],
        };
      }
    });

    if (event.id) {
      resolvedByEvent.set(event.id, resolved);
    }
  });

  return resolvedByEvent;
};

// The person/company who performed the action, for the timeline's "who" column. Bare name (no
// "by" prefix) so it reads like the Project/Order timelines; falls back to System for automation.
export const actorName = (event: PricingEvent) => {
  if (!event.actor) {
    return t('loads.pricing.actor_system');
  }

  return event.actor.name ?? event.actor.type;
};

// Humanize a pricing dimension into a readable label. Historical events still carry the legacy
// *_cents column names, so strip that suffix before turning snake_case into a capitalized phrase
// ("customer_rate_value" -> "Customer rate value").
const dimLabel = (dim: string) => {
  const words = dim.replace(/_cents$/, '').replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const EMPTY_CHANGE_VALUE = '—';

const ZERO_CHANGE_VALUE = '0';

// Rate types whose value is a percentage; every other rate type is a dollar amount.
const PERCENT_RATE_TYPES = new Set(['RatePercentOfTotal', 'RateCommission']);

interface DimUnit {
  prefix?: string;
  suffix?: string;
}

// Unit ($ / %) for a group-stripped scalar dim (the group prefix — material_/tax_ — is already
// removed, so tax's `tax_rate_percentage` arrives as `rate_percentage`). Material prices and PW
// rates are dollars; the tax rate is a percentage. (Freight rate values are handled by rateRow.)
const unitForDim = (dim: string): DimUnit | null => {
  if (dim === 'rate_percentage') {
    return { suffix: '%' };
  }
  if (
    dim === 'rate_buy_price' ||
    dim === 'rate_sell_price' ||
    dim === 'pw_on_site_rate' ||
    dim === 'pw_total_fringe_rate'
  ) {
    return { prefix: '$' };
  }

  return null;
};

// Friendly rate-type name shown across the app ("RatePerTon" -> "Per Ton"), matching how the order
// card and billing info render it; falls back to the raw value if a type has no translation.
const rateTypeLabel = (rateType: string) =>
  t(`rate_types.${snakeCase(rateType)}`, { defaultValue: rateType });

// A rate value rendered with its unit and friendly type ("$5.50 Per Ton", "20% Commission"). The
// unit is % for commission-style rate types and $ otherwise; a nil rate shows the placeholder alone
// (no unit, no type).
const formatRate = (
  value: PricingEventChangeValue,
  nilPlaceholder: string,
  rateType?: string,
): string => {
  const unit: DimUnit =
    rateType && PERCENT_RATE_TYPES.has(rateType) ? { suffix: '%' } : { prefix: '$' };
  const amount = formatScalarValue(value, nilPlaceholder, unit);
  const typeLabel = rateType ? rateTypeLabel(rateType) : '';
  // Don't tack a rate type onto the em-dash that stands in for "no rate".
  if (!typeLabel || amount === EMPTY_CHANGE_VALUE) {
    return amount;
  }

  return `${amount} ${typeLabel}`;
};

// Fold a party's rate type + rate value into a single "Rate" row, showing the amount and friendly
// type on each side ("$5.50 Per Ton -> $6.00 Per Hour").
const rateRow = (
  rateValueChange: PricingEventChange,
  rateTypeFrom?: string,
  rateTypeTo?: string,
): PricingChangeRow => {
  const nilPlaceholder =
    isNumericValue(rateValueChange.from) || isNumericValue(rateValueChange.to)
      ? ZERO_CHANGE_VALUE
      : EMPTY_CHANGE_VALUE;

  return {
    label: t('loads.pricing.rate'),
    before: formatRate(rateValueChange.from, nilPlaceholder, rateTypeFrom),
    after: formatRate(rateValueChange.to, nilPlaceholder, rateTypeTo),
  };
};

// A value is numeric if it is a number or a string that parses as a finite number. Used to decide
// how a nil side renders: a nil rate/price is zero, but a nil non-numeric field (e.g. unit of
// measure) is genuinely absent.
const isNumericValue = (value: PricingEventChangeValue): boolean =>
  typeof value === 'number' ||
  (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value)));

// Scalar dims carry string/number/boolean values; render them as a string with any unit applied. A
// nil side shows 0 for a numeric change (a nil rate is zero) and the em-dash otherwise, and the
// em-dash is never decorated with a unit. (Add-on array values route through addOnChangeRows.)
const formatScalarValue = (
  value: PricingEventChangeValue,
  nilPlaceholder: string,
  unit: DimUnit | null,
): string => {
  const base = value === null || value === undefined ? nilPlaceholder : String(value);
  if (!unit || base === EMPTY_CHANGE_VALUE) {
    return base;
  }

  // Currency amounts always show 2 decimals ($20.00, not the raw "20.0" the API sends). Non-numeric
  // and non-currency values (e.g. a percentage) render as-is.
  const display =
    unit.prefix === '$' && Number.isFinite(Number(base)) ? Number(base).toFixed(2) : base;

  return unit.prefix ? `${unit.prefix}${display}` : `${display}${unit.suffix ?? ''}`;
};

const asAddOns = (value: PricingEventChangeValue): AddOnSnapshot[] =>
  Array.isArray(value) ? value : [];

// One change dimension, resolved to display fields shared by the list and table views. Scalar dims
// carry `before`/`after` values. Add-on rows set both to null, put the add-on name in `label`, and
// flag `isAddOn` so the table can prefix the name with an "Add-on" badge and show the outcome word.
export interface PricingChangeRow {
  label: string;
  before: string | null;
  after: string | null;
  isAddOn?: boolean;
}

// Add-on dimensions carry an array of add-on objects, not a scalar — a plain "from → to" would
// stringify to "[object Object]". Diff the arrays by name into one descriptive row per add-on. The
// label is just the add-on name; the "Add-on" badge is added at render time, and the outcome word
// goes in `after` so the "After" column always shows the result (before stays blank).
const addOnChangeRows = (change: PricingEventChange): PricingChangeRow[] => {
  const before = asAddOns(change.from);
  const after = asAddOns(change.to);
  const beforeByName = new Map(before.map((addOn) => [addOn.name, addOn]));
  const afterByName = new Map(after.map((addOn) => [addOn.name, addOn]));
  const rows: PricingChangeRow[] = [];

  before.forEach((addOn) => {
    if (!afterByName.has(addOn.name)) {
      rows.push({
        label: addOn.name,
        before: null,
        after: t('loads.pricing.add_on_removed'),
        isAddOn: true,
      });
    }
  });
  after.forEach((addOn) => {
    const previous = beforeByName.get(addOn.name);
    if (!previous) {
      rows.push({
        label: addOn.name,
        before: null,
        after: t('loads.pricing.add_on_added'),
        isAddOn: true,
      });
    } else if (JSON.stringify(previous) !== JSON.stringify(addOn)) {
      rows.push({
        label: addOn.name,
        before: null,
        after: t('loads.pricing.add_on_changed'),
        isAddOn: true,
      });
    }
  });

  return rows;
};

// One change → one or more display rows. The group prefix is stripped from the label because the
// group header already names it ("customer_add_ons_resolution" -> "Add ons resolution",
// "material_rate_buy_price" -> "Rate buy price"). Billing-party rate type/value are folded into a
// single "Rate" row upstream, so they never reach here.
const changeRows = (change: PricingEventChange): PricingChangeRow[] => {
  if (change.dim.endsWith('_add_ons')) {
    return addOnChangeRows(change);
  }

  const group = groupOfDim(change.dim);
  const dimWithoutGroup = group ? change.dim.slice(group.length + 1) : change.dim;
  const nilPlaceholder =
    isNumericValue(change.from) || isNumericValue(change.to)
      ? ZERO_CHANGE_VALUE
      : EMPTY_CHANGE_VALUE;

  return [
    {
      label: dimLabel(dimWithoutGroup),
      before: formatScalarValue(change.from, nilPlaceholder, unitForDim(dimWithoutGroup)),
      after: formatScalarValue(change.to, nilPlaceholder, unitForDim(dimWithoutGroup)),
    },
  ];
};

// A group's change rows, for one grouped block. `party` is the group key (billing party or a
// category like material/tax); null for any other shared dim.
interface PricingChangeGroup {
  party: PricingGroup | null;
  rows: PricingChangeRow[];
}

// Group an event's changes: customer, vendor, driver, material, tax, then any other shared dims
// last. `effectiveRateTypes` supplies each billing party's before/after rate type (with the
// carried-forward type for value-only events); when omitted, the rate type is read from this event's
// own `*_rate_type` change, so the function still works standalone.
export const groupChangeRowsByParty = (
  changes: PricingEventChange[],
  effectiveRateTypes: EffectiveRateTypes = {},
): PricingChangeGroup[] => {
  const changesByGroup = new Map<PricingGroup | null, PricingEventChange[]>();

  changes.forEach((change) => {
    if (isHiddenDim(change.dim)) {
      return;
    }
    const group = groupOfDim(change.dim);
    const existing = changesByGroup.get(group) ?? [];
    existing.push(change);
    changesByGroup.set(group, existing);
  });

  const rowsForGroup = (group: PricingGroup | null): PricingChangeRow[] => {
    const groupChanges = changesByGroup.get(group) ?? [];
    const rows: PricingChangeRow[] = [];

    // Fold a billing party's rate type + value into one "Rate" row (material/tax have no such dims).
    if (group && isParty(group)) {
      const rateValueChange = groupChanges.find(
        (change) => change.dim === `${group}_rate_value`,
      );
      const rateTypeChange = groupChanges.find(
        (change) => change.dim === `${group}_rate_type`,
      );
      const rateTypes = effectiveRateTypes[group] ?? {
        from: typeof rateTypeChange?.from === 'string' ? rateTypeChange.from : undefined,
        to: typeof rateTypeChange?.to === 'string' ? rateTypeChange.to : undefined,
      };
      if (rateValueChange) {
        rows.push(rateRow(rateValueChange, rateTypes.from, rateTypes.to));
      } else if (rateTypeChange) {
        // Rate type changed with no value diff — show the type change on its own so it isn't lost.
        rows.push({
          label: t('loads.pricing.rate'),
          before: rateTypes.from ? rateTypeLabel(rateTypes.from) : EMPTY_CHANGE_VALUE,
          after: rateTypes.to ? rateTypeLabel(rateTypes.to) : EMPTY_CHANGE_VALUE,
        });
      }
    }

    // The rate type/value dims are folded into the "Rate" row above; render every other dim as-is.
    groupChanges.forEach((change) => {
      const isFoldedRateDim =
        group !== null &&
        isParty(group) &&
        (change.dim === `${group}_rate_type` || change.dim === `${group}_rate_value`);
      if (!isFoldedRateDim) {
        rows.push(...changeRows(change));
      }
    });

    return rows;
  };

  const groups: PricingChangeGroup[] = [];
  GROUP_ORDER.forEach((group) => {
    const rows = rowsForGroup(group);
    if (rows.length) {
      groups.push({ party: group, rows });
    }
  });

  const sharedRows = rowsForGroup(null);
  if (sharedRows.length) {
    groups.push({ party: null, rows: sharedRows });
  }

  return groups;
};

// Actions that ARE the event — there is no rate diff to describe, so the row stands on its own.
// A lock event's whole diff is `*_locked` markers, which `isHiddenDim` filters, so without this
// guard it would resolve to zero change rows and be dropped as noise (see below).
const MILESTONE_ACTIONS: PricingEventAction[] = ['freeze'];

// Whether the timeline should show an event. Three cases qualify: it has at least one change row,
// it carries no `changes` at all (a summary-only event — the table renders its summary row), or it
// is a milestone whose only dims are hidden markers. What we drop is the in-between: an event that
// HAS changes but they all resolve to nothing (only hidden dims like site_rate_schedule_id, or a
// no-op diff) — that's the noise.
export const eventHasVisibleChanges = (event: PricingEvent): boolean =>
  groupChangeRowsByParty(event.changes ?? []).length > 0 ||
  !event.changes?.length ||
  MILESTONE_ACTIONS.includes(event.action);

/* ---------------------------------------------------------------------------
 * Derived headline + context line
 *
 * The backend `summary` is never rendered. It is engineer-speak ("Load re-matched with its own
 * context"), it is ambiguous exactly where it matters most (one `load_divergence` string covers two
 * OPPOSITE transitions), it cannot carry the scope word / job ref / reach count, and changing a word
 * of it needs a backend deploy. Everything below is derived from `action`, the diff's dimensions,
 * and `ruleName`.
 *
 * Those three signals are HEURISTICS, not contract: scope comes from `subject_type`, the cascade
 * cause from the ABSENCE of `ruleName`, and the kind from dim prefixes. A backend `cause` enum would
 * replace all three with a lookup; it is deliberately not requested yet.
 * ------------------------------------------------------------------------- */

// A Load reads as "Job": a load's rates sit inside a job, and load-grain copy is out of scope
// (the rows are withheld behind `load_level_pricing` anyway). Without this an unranked or load
// subject would render a blank scope word.
const isOrderGrain = (event: PricingEvent) => event.subjectType === 'Order';

const scopeLabel = (event: PricingEvent): string =>
  isOrderGrain(event) ? t('loads.pricing.scope.order') : t('loads.pricing.scope.job');

// Changes that describe what actually moved — internal bookkeeping and lock markers excluded.
const substantiveChanges = (event: PricingEvent): PricingEventChange[] =>
  (event.changes ?? []).filter(
    (change) => !isInternalDim(change.dim) && !isLockMarkerDim(change.dim),
  );

// A lock event's diff is made up ENTIRELY of lock markers (plus internal bookkeeping). That is what
// separates it from a rate change whose diff merely happens to include a marker.
const isLockEvent = (event: PricingEvent): boolean => {
  const changes = event.changes ?? [];

  return (
    changes.some((change) => isLockMarkerDim(change.dim)) &&
    changes.every((change) => isLockMarkerDim(change.dim) || isInternalDim(change.dim))
  );
};

// The billing party a lock applied to, read off the marker dims. `ApplyInvoiceApprovalLockJob`
// stamps one side per call, so a lock event normally names exactly one party; anything else falls
// back to the scope word rather than guessing.
const lockedParty = (event: PricingEvent): PricingParty | null => {
  const parties = new Set(
    (event.changes ?? [])
      .filter((change) => isLockMarkerDim(change.dim))
      .map((change) => groupOfDim(change.dim))
      .filter((group): group is PricingParty => group !== null && isParty(group)),
  );

  return parties.size === 1 ? [...parties][0] : null;
};

// The rate kind named in the headline, or null for the generic "rates updated". A kind word is used
// only when the change is CONFINED to that kind — a rate-card cascade re-stamps the whole order
// context and its diff spans several, where naming one would be a lie. No side (customer / vendor /
// driver) is ever named: the change rows below the headline carry it.
const headlineKind = (event: PricingEvent): 'tax' | 'material' | 'add_ons' | null => {
  const changes = substantiveChanges(event);
  if (!changes.length) {
    return null;
  }

  const groups = new Set(changes.map((change) => groupOfDim(change.dim)));
  if (groups.size === 1) {
    const [onlyGroup] = [...groups];
    if (onlyGroup === 'tax' || onlyGroup === 'material') {
      return onlyGroup;
    }
  }

  if (changes.every((change) => change.dim.endsWith('_add_ons'))) {
    return 'add_ons';
  }

  return null;
};

// Which job(s) the entry touched. Exactly one → name it, because a `TRD-` reference is more useful
// than the count. Two or more → count them. Zero → nothing, the action stayed at the order.
// A happy consequence of naming the single case: the count never needs an i18next plural form.
//
// Read from the merged refs, never from the winner's own `subjectJobRef` — an order-level cascade
// that reached exactly one job is named by its Order record, which has no job ref.
const reachSuffix = ({ jobRefs }: MergedPricingEntry): string => {
  if (jobRefs.length === 1) {
    return t('loads.pricing.suffix.job', { ref: jobRefs[0] });
  }
  if (jobRefs.length > 1) {
    return t('loads.pricing.suffix.jobs', { count: jobRefs.length });
  }

  return '';
};

/**
 * The entry's headline: what changed, at what scope, and which job(s) it reached. Replaces the
 * backend `summary`.
 */
export const eventHeadline = (entry: MergedPricingEntry): string => {
  const { event } = entry;
  const scope = scopeLabel(event);
  const party = lockedParty(event);
  const partyLabel = party ? groupLabel(party) : null;

  const headline = (() => {
    if (event.action === 'refused') {
      return partyLabel
        ? t('loads.pricing.headline.blocked', { party: partyLabel })
        : t('loads.pricing.headline.blocked_no_party', { scope });
    }

    // `freeze` always reads as a lock, marker dims or not — a lock with an empty diff must not fall
    // through to "rates updated". The un-approval path releases the same markers but arrives as
    // `manual_edit`, so there it is the all-markers diff that identifies it.
    if (event.action === 'freeze' || isLockEvent(event)) {
      const key = event.action === 'freeze' ? 'locked' : 'unlocked';

      return partyLabel
        ? t(`loads.pricing.headline.${key}`, { party: partyLabel })
        : t(`loads.pricing.headline.${key}_no_party`, { scope });
    }

    if (event.action === 'backfill') {
      return t('loads.pricing.headline.imported', { scope });
    }

    const kind = headlineKind(event);
    if (kind === 'add_ons') {
      return t('loads.pricing.headline.add_ons', { scope });
    }

    if (event.action === 'manual_edit') {
      return t('loads.pricing.headline.manual', { scope });
    }

    // A generic word interpolated into the kind sentence reads "Order rate rates updated", so the
    // kindless case gets its own key rather than a `kind.default`.
    return kind
      ? t('loads.pricing.headline.updated_kind', {
          scope,
          kind: t(`loads.pricing.kind.${kind}`),
        })
      : t('loads.pricing.headline.updated', { scope });
  })();

  const suffix = reachSuffix(entry);

  return suffix ? `${headline} ${suffix}` : headline;
};

// A lock or refusal is ABOUT the lock, not about which card applied, so those lines win even when
// the record still carries a rule. Shared by the context line and the Matched-on row so the two
// can't disagree about whether this event shows a rate card.
const showsRateCard = (event: PricingEvent) =>
  event.action !== 'refused' &&
  event.action !== 'freeze' &&
  !isLockEvent(event) &&
  Boolean(event.ruleName);

/**
 * The muted line under the headline: WHY the rates changed. Empty string when the payload does not
 * determine a cause — never a guess, and never the backend `summary`.
 */
export const eventContextLine = (event: PricingEvent): string => {
  if (event.action === 'refused') {
    const party = lockedParty(event);

    return party
      ? t('loads.pricing.context.refused_locked', { party: groupLabel(party) })
      : t('loads.pricing.context.locked_by_approval');
  }

  if (event.action === 'freeze') {
    return t('loads.pricing.context.locked_by_approval');
  }

  if (isLockEvent(event)) {
    return t('loads.pricing.context.invoice_unapproved');
  }

  if (showsRateCard(event)) {
    // A cascade means the card itself was edited; a match means this pricing was stamped FROM it.
    // `escapeValue: false` because a card name containing "/" would otherwise render as `&#x2F;`
    // (i18n.ts sets escaping globally); React still escapes on render.
    const key = event.action === 'cascade' ? 'rate_card_edited' : 'from_rate_card';

    return t(`loads.pricing.context.${key}`, {
      name: event.ruleName,
      interpolation: { escapeValue: false },
    });
  }

  // A cascade with no card behind it came from the order's own tax/material edit. Said explicitly,
  // because an absent line reads as "we don't know" rather than "no card drove this".
  if (event.action === 'cascade') {
    return t('loads.pricing.context.changed_on_order');
  }

  return '';
};

/**
 * What made the rate card apply, shown beneath the context line. `null` when this event names no
 * card and the row is omitted entirely; an EMPTY array is a catch-all card, which is an answer
 * rather than an absence and gets its own line.
 */
export const eventMatchedConditions = (event: PricingEvent) =>
  showsRateCard(event) ? event.ruleConditions : null;

// An order-grain change was not itself matched against a load, so "Matched on" overstates it —
// the row is stating the card's criteria. Job- and load-grain rows really did match, and keep the
// original wording.
export const matchedConditionsLabel = (event: PricingEvent) =>
  isOrderGrain(event)
    ? t('loads.pricing.context.match_criteria')
    : t('loads.pricing.context.matched_on');

/** Same distinction as the label above, for the card that carries no conditions at all. */
export const matchedCatchAllLine = (event: PricingEvent) =>
  isOrderGrain(event)
    ? t('loads.pricing.context.match_criteria_catch_all')
    : t('loads.pricing.context.matched_on_catch_all');

/** The dimension a condition constrains, in the customer-facing words the rate card editor uses. */
const conditionTypeLabel = (type: PricingEventRuleConditionType) => {
  const labelByType: Record<PricingEventRuleConditionType, string> = {
    [PricingEventRuleConditionType.PICKUP]: t('form_fields.pickup'),
    [PricingEventRuleConditionType.DROPOFF]: t('form_fields.dropoff'),
    [PricingEventRuleConditionType.MATERIAL]: t('form_fields.material'),
    [PricingEventRuleConditionType.TRUCK]: t('form_fields.truck'),
  };

  return labelByType[type];
};

export const matchedConditionLabel = (condition: PricingEventRuleCondition) =>
  // `escapeValue: false` because a site or material name containing "/" would otherwise render as
  // `&#x2F;` (i18n.ts sets escaping globally); React still escapes on render.
  t('loads.pricing.context.matched_on_condition', {
    dimension: conditionTypeLabel(condition.type),
    value: condition.label,
    interpolation: { escapeValue: false },
  });
